import classNames from 'classnames';
// import MaterialReactTable, {
//   MaterialReactTableProps,
// } from 'material-react-table';

import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_Row,
  MRT_ToolbarInternalButtons,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, Button, IconButton, Menu } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useState } from 'react';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { Download, PrintOutlined } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import { MenuProps } from '@mui/material/Menu';
import { createTheme, ThemeProvider, useTheme } from '@mui/material';
import { useMemo } from 'react';
import React from 'react';

export interface TableProps {
  useCallbackClickRow?: any;
  inner?: boolean;
  name: string;
  columns: any;
  data: Record<string, any>[];
  onSelectRow?: any;
  initialState?: object;
  enableBottomToolbar?: boolean;
  enableTopToolbar?: boolean;
  enableColumnActions?: boolean;
  enableColumnFilters?: boolean;
  enableSorting?: boolean;
  enableExpanding?: boolean;
  enableExpandAll?: boolean;
  columnFilterDisplayMode: string;
  enableFacetedValues?: boolean;
  enableRowSelection?: boolean;
  enableToolbarInternalActions?: boolean;
  positionToolbarAlertBanner?: string;
  advancedTable?: boolean;
  // manualPagination?: boolean;
  // manualSorting?: boolean;
}

export function Table(props: any) {
  const {
    useCallbackClickRow,
    enableBottomToolbar = true,
    enableTopToolbar = true,
    enableColumnFilters = false,
    enableColumnActions = false,
    enableSorting = true,
    inner = false,
    onSelectRow,
    enableExpanding,
    enableExpandAll,
    enableFacetedValues = false,
    enableToolbarInternalActions = false,
    positionToolbarAlertBanner = 'bottom',
    columnFilterDisplayMode = 'custom',
    advancedTable = false,
    name = 'material-table',
  } = props;
  const currentPage = localStorage.getItem(`table-${name}-page-index`) || 0;
  const [firstLaunch, setFirstLaunch] = React.useState(true);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [columnFilterOpen, setColumnFilterOpen] = useState(true);

  const table = useMaterialReactTable({
    data: props.data,
    columns: props.columns,
    initialState: props.initialState,
    columnFilterDisplayMode: columnFilterDisplayMode,
    enableBottomToolbar: enableBottomToolbar,
    positionToolbarAlertBanner: positionToolbarAlertBanner,
    enableColumnActions: enableColumnActions,
    enableColumnFilters: enableColumnFilters,
    enableSorting: enableSorting,
    enablePinning: true,
    enableTopToolbar: enableTopToolbar,
    enableExpanding: enableExpanding,
    enableExpandAll: enableExpandAll,
    muiPaginationProps(props) {
      const page = props.table.getState().pagination.pageIndex;
      if (!firstLaunch) {
        localStorage.setItem(`table-${name}-page-index`, String(page));
      }
      if (firstLaunch) {
        props.table.setPageIndex(() => Number(currentPage));
      }
      setFirstLaunch(false);
    },
    defaultColumn: {
      minSize: 5,
      maxSize: 9001, //allow columns to get larger than default
      //size: 260, //make columns wider by default
    },
    enableToolbarInternalActions: enableToolbarInternalActions,
    positionGlobalFilter: 'left',
    enableFacetedValues: enableFacetedValues,
    muiBottomToolbarProps: {
      className: classNames({ '!hidden': props.enablePagination === false }),
    },
    muiTopToolbarProps: ({ table }) => ({
      className:
        '!bg-gray-100 !border-gray-200 border-b-[1px] !min-h-[82px] !p-5',
    }),
    muiTablePaperProps: ({ table }) => ({
      style: {
        zIndex: table.getState().isFullScreen ? 10000 : undefined,
        position: table.getState().isFullScreen ? 'fixed' : undefined,
        top: table.getState().isFullScreen ? '143px' : undefined,
        left: table.getState().isFullScreen ? '0' : undefined,
        right: table.getState().isFullScreen ? '0' : undefined,
        bottom: table.getState().isFullScreen ? '0' : undefined,
        width: table.getState().isFullScreen ? '100%' : undefined,
        height: table.getState().isFullScreen
          ? 'calc(100vh - 143px)'
          : undefined,
        overflow: table.getState().isFullScreen ? 'auto' : undefined,
      },
      elevation: 0,
      className: classNames(
        '!rounded-lg !border-[1px] !border-gray-200 !overflow-hidden',
        { '!rounded-none !border-none': inner }
      ),
    }),
    muiTableHeadCellProps: {
      className:
        '!font-nunito !bg-gray-50 !text-sm !uppercase !text-gray-700 !py-2 !pl-2 !pr-0 !align-middle',
      sx: {
        '.Mui-TableHeadCell-Content-Wrapper': { minWidth: '0px' },
      },
    },
    muiTableBodyRowProps: (data: any) => ({
      onClick: (event: any) => {
        //preventing row click if there other custom component inside cell
        if (
          String(event?.target?.className)?.includes('MuiTableCell-root') &&
          (useCallbackClickRow || onSelectRow)
        ) {
          useCallbackClickRow && useCallbackClickRow(data.row?.original);
          onSelectRow && onSelectRow(data.row?.index);
        }
      },
      className: classNames(
        'relative z-0 hover:z-10 !font-nunito !text-base !text-gray-900 !overflow-visible !pl-2 !pr-0 hover:!outline-primary-900',
        { '!cursor-pointer': useCallbackClickRow || onSelectRow }
      ),
    }),
    muiTableBodyCellProps: (data: any) => ({
      onClick: (event: any) => {
        //preventing row click if there other custom component inside cell
        if (
          String(event?.target?.className)?.includes('MuiTableCell-root') &&
          (useCallbackClickRow || onSelectRow)
        ) {
          useCallbackClickRow && useCallbackClickRow(data.row?.original);
          onSelectRow && onSelectRow(data.row?.index);
        }
      },
      className: classNames(
        '!font-nunito !text-base !text-gray-900 !overflow-visible !pl-2 !pr-0 hover:!outline-primary-900',
        { '!cursor-pointer': useCallbackClickRow || onSelectRow }
      ),
    }),
    ...props,
    muiSearchTextFieldProps: {
      placeholder: 'Search',
      variant: 'outlined',
      margin: 'none',
      ...(!advancedTable && {
        className:
          '!w-[600px] !my-3 bg-white !w-[calc(100vw-80px)] md:!w-[600px]',
        sx: {
          '> .MuiOutlinedInput-root': {
            '& > fieldset': { borderRadius: '10px', backgroundColor: '#fff' },
            '& > input': { padding: '12px 0', backgroundColor: '#fff' },
            '& > svg': { width: '18px', height: '18px' },
          },
        },
      }),
      ...(advancedTable && {
        className: '!w-[500px] bg-white',
        sx: {
          '> .MuiOutlinedInput-root': {
            '& > fieldset': { borderRadius: '10px' },
            '& > input': {
              borderRadius: '10px',
              padding: '10px 0',
              backgroundColor: '#fff',
            },
            '& > svg': { width: '18px', height: '18px' },
          },
        },
      }),
      ...(props.muiSearchTextFieldProps || {}),
    },

    muiFilterTextFieldProps: ({ column }) => ({
      variant: 'outlined',
      margin: 'none',
      className: 'bg-white font-nunito',
      sx: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderRadius: '5px', padding: '0' },
          '& input': {
            width: '100%',
            borderRadius: '5px',
            padding: '8px 5px',
            fontSize: '12px',
          },
          '& .MuiSelect-select': {
            borderRadius: '5px',
            padding: '8px 5px',
            fontSize: '12px',
          },
        },
        '& .MuiPaper-root': {
          padding: 0,
          borderRadius: 6,
          minWidth: 180,
          fontSize: 12,
          color: 'rgb(55, 65, 81)',
          '& .MuiMenu-list': {
            padding: '0',
            fontSize: 12,
          },
          '& .MuiMenuItem-root': {
            fontSize: 12,
            padding: 0,
            '& .MuiSvgIcon-root': {
              fontSize: 12,
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          },
        },
      },
    }),
  });

  const globalTheme = useTheme(); //(optional) if you already have a theme defined in your app root, you can import here

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: 'rgb(59 125 112)',
          },
        },
        typography: {
          fontFamily: [
            'Nunito',
            'ui-sans-serif',
            'system-ui',
            '-apple-system',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
        },
      }),
    [globalTheme]
  );

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: `data-export-${new Date().toISOString()}`,
  });

  const normalizeValue = (val: any) => {
    if (val == null) return '';
    if (React.isValidElement(val)) {
      const children = (val as any).props?.children;
      return typeof children === 'string' ? children : String(children ?? '');
    }
    if (val instanceof Date) return val.toLocaleDateString();
    if (typeof val === 'string') {
      const d = new Date(val);
      if (!isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(val)) {
        return d.toLocaleDateString();
      }
      return val;
    }
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const buildExportObjectFromRow = (row: MRT_Row<any>) => {
    const visibleCells = row.getVisibleCells();
    const out: Record<string, any> = {};
    visibleCells.forEach((cell) => {
      const header =
        typeof cell.column.columnDef.header === 'string'
          ? (cell.column.columnDef.header as string)
          : cell.column.id;
      const value = cell.getValue();
      if (header !== 'Select') out[header] = normalizeValue(value);
    });
    return out;
  };

  const buildExportObjectFromItem = (item: any) => {
    const out: Record<string, any> = {};
    const visibleCols = table.getVisibleLeafColumns();
    visibleCols.forEach((col) => {
      const header =
        typeof col.columnDef.header === 'string'
          ? (col.columnDef.header as string)
          : col.id;
      if (header !== 'Select') {
        let value: any = undefined;
        const accessorFn = (col.columnDef as any).accessorFn as
          | ((row: any) => any)
          | undefined;
        const accessorKey = (col.columnDef as any).accessorKey as
          | string
          | undefined;
        try {
          if (accessorFn) value = accessorFn(item);
          else if (accessorKey) value = item?.[accessorKey];
          else value = item?.[col.id as any];
        } catch (e) {
          value = accessorKey ? item?.[accessorKey] : item?.[col.id as any];
        }
        out[header] = normalizeValue(value);
      }
    });
    return out;
  };

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const dataImport = rows.map((row) => buildExportObjectFromRow(row));
    handleClose();
    const csv = generateCsv(csvConfig)(dataImport);
    download(csvConfig)(csv);
  };

  const handleExportData = () => {
    const dataImport = props.data.map((item: any) =>
      buildExportObjectFromItem(item)
    );
    handleClose();
    const csv = generateCsv(csvConfig)(dataImport);
    download(csvConfig)(csv);
  };

  const StyledMenu = styled((props: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: 6,
      fontSize: 12,
      marginTop: theme.spacing(1),
      minWidth: 180,
      fontFamily: 'Nunito',
      color: 'rgb(55, 65, 81)',
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      '& .MuiMenu-list': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        fontSize: 14,
        '& .MuiSvgIcon-root': {
          fontSize: 12,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        '&:active': {},
      },
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[300],
      }),
    },
  }));

  // @ts-ignore
  // @ts-ignore
  return (
    <ThemeProvider theme={tableTheme}>
      {advancedTable && (
        <Box
          style={{
            position: table.getState().isFullScreen ? 'fixed' : undefined,
            top: table.getState().isFullScreen ? '74px' : undefined,
            left: table.getState().isFullScreen ? 0 : undefined,
            right: table.getState().isFullScreen ? 0 : undefined,
            width: table.getState().isFullScreen ? '100%' : undefined,
          }}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              flexDirection: 'row',
              gap: '10px',
              justifyContent: 'space-between',
              padding: '10px 10px',
              '@media max-width: 768px': {
                flexDirection: 'column',
              },
            })}
          >
            <Box>
              <div className="flex flex-row items-center gap-2 py-2">
                <Button
                  variant="contained"
                  size="small"
                  id="basic-button"
                  startIcon={<Download />}
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  sx={{
                    color: '#fff',
                    textTransform: 'none',
                  }}
                  className="font-nunito text-sm"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                >
                  Export data
                </Button>
                <StyledMenu
                  anchorEl={anchorEl}
                  open={open}
                  className="font-nunito text-sm"
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                >
                  <MenuItem className="font-nunito" onClick={handleExportData}>
                    Export CSV (all Data)
                  </MenuItem>
                  <MenuItem
                    disabled={
                      !table.getIsSomeRowsSelected() &&
                      !table.getIsAllRowsSelected()
                    }
                    onClick={() =>
                      handleExportRows(table.getSelectedRowModel().rows)
                    }
                  >
                    Export CSV (selected rows)
                  </MenuItem>
                </StyledMenu>
              </div>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
              <MRT_GlobalFilterTextField
                sx={{ marginRight: 0 }}
                table={table}
              />
              {/* add custom button to print table  */}
              <IconButton
                onClick={() => {
                  window.print();
                }}
              >
                <PrintOutlined />
              </IconButton>
              <MRT_ToolbarInternalButtons table={table} />
            </Box>
          </Box>
        </Box>
      )}
      <MaterialReactTable table={table} />
    </ThemeProvider>
  );
}

export default Table;
